// ============================================
// JOBS LIST PAGE
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  MoreVertical,
  Eye,
  Edit,
  Ban,
  CheckCircle,
  Building2,
  DollarSign,
  Calendar,
  Users,
  Star,
} from 'lucide-react';
import type { Job, JobStatus, Department, VesselType } from '@crewatsea/shared-types';
import {
  PageHeader,
  DataTable,
  FilterBar,
  FilterSelect,
  StatusBadge,
  ConfirmDialog,
} from '@/components';
import { adminApi } from '@/services/api';
import { formatDate, formatNumber, cn } from '@/utils';

export const Route = createFileRoute('/_authenticated/jobs/')({
  component: JobsPage,
});

function JobsPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [featuredFilter, setFeaturedFilter] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    job: Job | null;
    action: 'pause' | 'activate';
  }>({ open: false, job: null, action: 'pause' });

  // İlanları getir
  const { data: rawData, isLoading } = useQuery({
    queryKey: ['admin', 'jobs', { page, limit, status: statusFilter, department: departmentFilter, search, featured: featuredFilter }],
    queryFn: async () => {
      const response = await adminApi.getJobs({
        page,
        limit,
        status: statusFilter || undefined,
        department: departmentFilter || undefined,
        search: search || undefined,
        isFeatured: featuredFilter === 'true' ? true : featuredFilter === 'false' ? false : undefined,
      } as any);
      return (response as any)?.data || response;
    },
  });

  // Sponsorlu ilan sayısı
  const { data: sponsoredCount } = useQuery({
    queryKey: ['admin', 'jobs', 'sponsored-count'],
    queryFn: async () => {
      const response = await adminApi.getJobs({ limit: 1, isFeatured: true } as any);
      const d = (response as any)?.data || response;
      return d?.meta?.total ?? 0;
    },
  });

  const data = rawData;

  // Durum güncelleme mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => adminApi.updateJob(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'jobs'] });
      setConfirmDialog({ open: false, job: null, action: 'pause' });
    },
  });

  // Sponsorlu toggle mutation
  const toggleFeaturedMutation = useMutation({
    mutationFn: ({ id, isFeatured }: { id: string; isFeatured: boolean }) =>
      adminApi.updateJob(id, { isFeatured }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'jobs'] });
    },
  });

  const handleConfirmAction = () => {
    if (!confirmDialog.job) return;
    const newStatus = confirmDialog.action === 'pause' ? 'PAUSED' : 'ACTIVE';
    updateStatusMutation.mutate({ id: confirmDialog.job.id, status: newStatus });
  };

  const departmentLabels: Record<Department, string> = {
    DECK: 'Güverte',
    ENGINE: 'Makine',
    ELECTRICAL: 'Elektrik',
    CATERING: 'Catering',
    OTHER: 'Diğer',
  };

  const columns = [
    {
      key: 'job',
      header: 'İlan',
      render: (job: Job & { company?: { name: string; isVerified: boolean } }) => (
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-gray-900">{job.title}</p>
            {(job as any).isFeatured && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700">
                <Star className="h-3 w-3 fill-amber-500" />
                Sponsorlu
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 text-sm text-gray-500 mt-0.5">
            <Building2 className="h-3.5 w-3.5" />
            <span>{job.company?.name || '-'}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'department',
      header: 'Departman',
      render: (job: Job) => (
        <span
          className={cn(
            'px-2.5 py-0.5 text-xs font-medium rounded-full',
            job.department === 'DECK' && 'bg-blue-100 text-blue-800',
            job.department === 'ENGINE' && 'bg-green-100 text-green-800',
            job.department === 'ELECTRICAL' && 'bg-amber-100 text-amber-800',
            job.department === 'CATERING' && 'bg-purple-100 text-purple-800',
            job.department === 'OTHER' && 'bg-gray-100 text-gray-800'
          )}
        >
          {departmentLabels[job.department]}
        </span>
      ),
    },
    {
      key: 'salary',
      header: 'Maaş',
      render: (job: Job) => (
        <div className="flex items-center gap-1 text-gray-600">
          <DollarSign className="h-4 w-4 text-gray-400" />
          <span>
            {job.salaryMin && job.salaryMax
              ? `${formatNumber(job.salaryMin)} - ${formatNumber(job.salaryMax)} ${job.salaryCurrency}`
              : job.salaryMin
                ? `${formatNumber(job.salaryMin)}+ ${job.salaryCurrency}`
                : '-'}
          </span>
        </div>
      ),
    },
    {
      key: 'contract',
      header: 'Sözleşme',
      render: (job: Job) => (
        <span className="text-gray-600">
          {job.contractDuration ? `${job.contractDuration} ay` : '-'}
        </span>
      ),
    },
    {
      key: 'applications',
      header: 'Başvurular',
      render: (job: any) => (
        <div className="flex items-center gap-1 text-gray-600">
          <Users className="h-4 w-4 text-gray-400" />
          <span>{job.applicationCount || job._count?.applications || 0}</span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Durum',
      render: (job: Job) => <StatusBadge status={job.status} dot />,
    },
    {
      key: 'expires',
      header: 'Bitiş Tarihi',
      render: (job: Job) => (
        <div className="flex items-center gap-1 text-gray-500">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span>{job.expiresAt ? formatDate(job.expiresAt) : '-'}</span>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      render: (job: Job) => (
        <div className="relative group">
          <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md">
            <MoreVertical className="h-4 w-4" />
          </button>
          <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-maritime shadow-maritime-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
            <div className="py-1">
              <Link
                to={`/jobs/${job.id}` as any}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Eye className="h-4 w-4" />
                Görüntüle
              </Link>
              <Link
                to={`/jobs/${job.id}/edit` as any}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Edit className="h-4 w-4" />
                Düzenle
              </Link>
              <button
                onClick={() => toggleFeaturedMutation.mutate({ id: job.id, isFeatured: !(job as any).isFeatured })}
                disabled={toggleFeaturedMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 text-sm text-amber-600 hover:bg-amber-50 w-full disabled:opacity-50"
              >
                <Star className={cn('h-4 w-4', (job as any).isFeatured && 'fill-amber-400')} />
                {(job as any).isFeatured ? 'Sponsorluyu Kaldır' : 'Sponsorlu Yap'}
              </button>
              <hr className="my-1" />
              {job.status === 'ACTIVE' ? (
                <button 
                  onClick={() => setConfirmDialog({ open: true, job, action: 'pause' })}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-amber-600 hover:bg-amber-50 w-full"
                >
                  <Ban className="h-4 w-4" />
                  Pasif Yap
                </button>
              ) : (
                <button 
                  onClick={() => setConfirmDialog({ open: true, job, action: 'activate' })}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-green-600 hover:bg-green-50 w-full"
                >
                  <CheckCircle className="h-4 w-4" />
                  Aktif Yap
                </button>
              )}
            </div>
          </div>
        </div>
      ),
    },
  ];

  const statusOptions = [
    { value: 'ACTIVE', label: 'Aktif' },
    { value: 'DRAFT', label: 'Taslak' },
    { value: 'PAUSED', label: 'Duraklatıldı' },
    { value: 'CLOSED', label: 'Kapatıldı' },
    { value: 'EXPIRED', label: 'Süresi Doldu' },
  ];

  const departmentOptions = [
    { value: 'DECK', label: 'Güverte' },
    { value: 'ENGINE', label: 'Makine' },
    { value: 'ELECTRICAL', label: 'Elektrik' },
    { value: 'CATERING', label: 'Catering' },
    { value: 'OTHER', label: 'Diğer' },
  ];

  const activeFiltersCount =
    (statusFilter ? 1 : 0) + (departmentFilter ? 1 : 0) + (search ? 1 : 0) + (featuredFilter ? 1 : 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="İlanlar"
        description={
          <span className="flex items-center gap-3">
            <span>Tüm iş ilanlarını yönetin</span>
            {(sponsoredCount ?? 0) > 0 && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                <Star className="h-3 w-3 fill-amber-500" />
                {sponsoredCount} sponsorlu ilan aktif
              </span>
            )}
          </span>
        }
      />

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="İlan başlığı ile ara..."
        filters={
          <>
            <FilterSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={statusOptions}
              placeholder="Tüm Durumlar"
            />
            <FilterSelect
              value={departmentFilter}
              onChange={setDepartmentFilter}
              options={departmentOptions}
              placeholder="Tüm Departmanlar"
            />
            <FilterSelect
              value={featuredFilter}
              onChange={setFeaturedFilter}
              options={[
                { value: 'true', label: '⭐ Sponsorlu' },
                { value: 'false', label: 'Sponsorsuz' },
              ]}
              placeholder="Tüm İlanlar"
            />
          </>
        }
        activeFiltersCount={activeFiltersCount}
        onClearFilters={() => {
          setSearch('');
          setStatusFilter('');
          setDepartmentFilter('');
          setFeaturedFilter('');
        }}
      />

      <DataTable
        columns={columns}
        data={data?.data || []}
        keyExtractor={(job) => job.id}
        loading={isLoading}
        emptyMessage="İlan bulunamadı"
        pagination={{
          page,
          limit,
          total: data?.meta?.total || 0,
          totalPages: data?.meta?.totalPages || 1,
          onPageChange: setPage,
          onLimitChange: setLimit,
        }}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) =>
          setConfirmDialog({ ...confirmDialog, open })
        }
        title={confirmDialog.action === 'pause' ? 'İlanı Pasif Yap' : 'İlanı Aktif Yap'}
        description={
          confirmDialog.action === 'pause'
            ? `"${confirmDialog.job?.title}" ilanını pasif yapmak istediğinize emin misiniz?`
            : `"${confirmDialog.job?.title}" ilanını aktif yapmak istediğinize emin misiniz?`
        }
        variant={confirmDialog.action === 'pause' ? 'warning' : 'success'}
        confirmText={confirmDialog.action === 'pause' ? 'Pasif Yap' : 'Aktif Yap'}
        onConfirm={handleConfirmAction}
        loading={updateStatusMutation.isPending}
      />
    </div>
  );
}
